import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertTriangle,
  CheckCircle2,
  Link2,
  Link2Off,
  ShieldCheck,
  Coins,
} from "lucide-react";
import FxMarketPill from "@/components/FxMarketPill";
import {
  capitalAccount,
  capitalConnect,
  capitalDisconnect,
  capitalStatus,
  formatApiError,
  toastApiError,
} from "@/lib/api";

const fmtMoney = (v, ccy = "USD") => {
  if (v == null) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: ccy,
    maximumFractionDigits: 2,
  });
};

export default function CapitalSettingsPage() {
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const s = await capitalStatus();
      setStatus(s);
      if (s?.connected) {
        try {
          setAccount(await capitalAccount());
        } catch (e) {
          setAccount({ _error: formatApiError(e) });
        }
      } else {
        setAccount(null);
      }
    } catch (e) {
      toastApiError(e, "Capital status failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const trimmedId = identifier.trim();
      const trimmedPw = password.trim();
      const trimmedKey = apiKey.trim();
      if (!trimmedId || !trimmedPw || !trimmedKey) {
        toast.error("Identifier, password, and API key are all required.");
        return;
      }
      const res = await capitalConnect({
        identifier: trimmedId,
        password: trimmedPw,
        api_key: trimmedKey,
      });
      toast.success(
        `Capital.com demo connected — account ${res.account_name || res.account_id}`,
      );
      setIdentifier("");
      setPassword("");
      setApiKey("");
      await refresh();
    } catch (err) {
      toastApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const disconnect = async () => {
    if (
      !window.confirm(
        "Disconnect Capital.com? Your Capital demo portfolio and its trade "
        + "history in NUMIROCK will be removed. Your actual Capital demo "
        + "account is untouched — close positions there separately if needed.",
      )
    ) {
      return;
    }
    setSubmitting(true);
    try {
      await capitalDisconnect();
      toast.success("Capital.com disconnected.");
      await refresh();
    } catch (err) {
      toastApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="capital-settings-loading"
        className="py-16 text-center text-sm text-[var(--aurion-muted)]"
      >
        Loading Capital.com status…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="capital-settings-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[var(--aurion-gold)]">
            Capital.com Demo
          </h1>
          <p className="mt-1 text-sm text-[var(--aurion-muted)]">
            Connect your Capital.com <strong>demo</strong> account to trade real
            spot gold, silver, and major FX pairs (EUR/USD, GBP/USD, USD/JPY) —
            all CFDs on demo money. NUMIROCK's base URL is hard-coded to{" "}
            <code className="text-[var(--aurion-text-dim)]">
              demo-api-capital.backend-capital.com
            </code>
            {" "}so live keys structurally cannot fire — they will simply fail
            to authenticate.
          </p>
        </div>
        <FxMarketPill testid="capital-market-pill" />
      </header>

      <Alert
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
        data-testid="capital-security-note"
      >
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Your credentials never leave the server as plain text</AlertTitle>
        <AlertDescription className="text-xs text-[var(--aurion-muted)]">
          The API key, custom password, and identifier are each Fernet-encrypted
          at rest. Session tokens (CST / X-SECURITY-TOKEN) live in memory only
          and are refreshed transparently. NUMIROCK never returns any of these
          in an API response — even to master accounts in cloak mode.
        </AlertDescription>
      </Alert>

      {status?.connected ? (
        <Card
          className="border-emerald-500/30 bg-[var(--aurion-panel)]/60"
          data-testid="capital-connected-card"
        >
          <CardHeader className="flex flex-row items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <CardTitle className="text-base uppercase tracking-widest text-emerald-300">
              Connected · Demo
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Field
              label="API Key"
              value={`•••• •••• •••• ${status.api_key_last4 || "????"}`}
              testid="capital-key-last4"
            />
            <Field
              label="Identifier"
              value={status.identifier_masked || "—"}
              testid="capital-identifier"
            />
            <Field
              label="Account ID"
              value={status.account_id || "—"}
              testid="capital-account-id"
            />
            <Field
              label="Account name"
              value={status.account_name || "—"}
              testid="capital-account-name"
            />
            <Field
              label="Type"
              value={status.account_type || status.environment || "DEMO"}
              testid="capital-account-type"
            />
            <Field
              label="Currency"
              value={status.currency || "USD"}
              testid="capital-currency"
            />
            {account && !account._error ? (
              <>
                <Field
                  label="Balance"
                  value={fmtMoney(account.balance, account.currency)}
                  testid="capital-balance"
                />
                <Field
                  label="Available"
                  value={fmtMoney(account.available, account.currency)}
                  testid="capital-available"
                />
                <Field
                  label="Deposit"
                  value={fmtMoney(account.deposit, account.currency)}
                  testid="capital-deposit"
                />
                <Field
                  label="P&L (session)"
                  value={fmtMoney(account.profit_loss, account.currency)}
                  testid="capital-pnl"
                />
                <Field
                  label="Open positions"
                  value={account.positions_count}
                  testid="capital-positions-count"
                />
              </>
            ) : account?._error ? (
              <div className="col-span-full">
                <Alert variant="destructive" data-testid="capital-account-error">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Capital.com account read failed</AlertTitle>
                  <AlertDescription>{account._error}</AlertDescription>
                </Alert>
              </div>
            ) : null}
            <div className="col-span-full">
              <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Verified epics
              </div>
              <div
                className="mt-1 flex flex-wrap gap-1 text-[10px] font-mono"
                data-testid="capital-epic-map"
              >
                {Object.entries(status.epic_map || {}).map(([sym, epic]) => (
                  <span
                    key={sym}
                    className="rounded-sm border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] px-1.5 py-[1px] text-[var(--aurion-text-dim)]"
                    data-testid={`capital-epic-${sym}`}
                  >
                    {sym} → {epic}
                  </span>
                ))}
              </div>
            </div>
            <div className="col-span-full flex justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                data-testid="capital-disconnect-btn"
                onClick={disconnect}
                disabled={submitting}
                className="border-[var(--aurion-red)]/40 text-[var(--aurion-red)] hover:bg-[var(--aurion-red)]/10"
              >
                <Link2Off className="mr-2 h-4 w-4" />
                Disconnect Capital.com
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card
          className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
          data-testid="capital-connect-card"
        >
          <CardHeader className="flex flex-row items-center gap-3">
            <Link2 className="h-5 w-5 text-[var(--aurion-gold)]" />
            <CardTitle className="text-base uppercase tracking-widest text-[var(--aurion-gold)]">
              Connect a demo account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4" autoComplete="off">
              <p className="text-xs text-[var(--aurion-muted)]">
                Generate an API key at{" "}
                <a
                  href="https://capital.com/trading/settings/api-integrations"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  Capital.com → Settings → API integrations
                </a>
                . Two-factor may be required; the API-key custom password is
                the one you set during key generation — NOT your regular
                platform login password.
              </p>
              <div>
                <Label
                  htmlFor="capital-identifier-input"
                  className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]"
                >
                  Login email (identifier)
                </Label>
                <Input
                  id="capital-identifier-input"
                  data-testid="capital-identifier-input"
                  type="email"
                  autoComplete="off"
                  spellCheck={false}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="you@example.com"
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label
                  htmlFor="capital-password-input"
                  className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]"
                >
                  API key custom password
                </Label>
                <Input
                  id="capital-password-input"
                  data-testid="capital-password-input"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="the password you set when generating the key"
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label
                  htmlFor="capital-apikey-input"
                  className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]"
                >
                  X-CAP-API-KEY
                </Label>
                <Input
                  id="capital-apikey-input"
                  data-testid="capital-apikey-input"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="the API key shown once at generation time"
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="submit"
                  data-testid="capital-connect-btn"
                  disabled={submitting}
                  className="bg-[var(--aurion-gold)] text-black hover:bg-[var(--aurion-gold)]/90"
                >
                  {submitting ? "Connecting…" : "Connect demo"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Alert
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
        data-testid="capital-demo-only"
      >
        <Coins className="h-4 w-4 text-[var(--aurion-gold)]" />
        <AlertTitle>Demo money only — by design</AlertTitle>
        <AlertDescription className="text-xs text-[var(--aurion-muted)]">
          v1 supports opening LONG CFD positions on gold, silver, EUR/USD,
          GBP/USD, and USD/JPY. Shorting is disabled. Sizing is conservative
          (no leverage abuse). Gold and major FX trade ~24/5 — trades attempted
          on weekends will fail with a clear "market closed" error from Capital.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
        {label}
      </div>
      <div className="mt-1 font-mono text-sm text-[var(--aurion-text)]" data-testid={testid}>
        {value ?? "—"}
      </div>
    </div>
  );
}
